import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import USGeoMap from "@/components/USGeoMap";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import NewsletterSignup from "@/components/NewsletterSignup";
import RunningBanner from "@/components/RunningBanner";
import PowerStudyBanner from "@/components/PowerStudyBanner";
import { Section, SectionHead, AdSlot, SourceLink } from "@/components/common";

const fade = { hidden: { opacity: 0, y: 16 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08 } }) };

export default function Home() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [news, setNews] = useState([]);
  const [newsStatus, setNewsStatus] = useState(null);
  const [claims, setClaims] = useState([]);
  const [explainers, setExplainers] = useState([]);
  const [q, setQ] = useState("");
  const [hover, setHover] = useState(null);

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/news?limit=5").then((r) => setNews(r.data));
    api.get("/news/status").then((r) => setNewsStatus(r.data));
    api.get("/claims").then((r) => setClaims(r.data));
    api.get("/explainers").then((r) => setExplainers(r.data));
  }, []);

  const matches = q.length > 0 ? [
    ...states.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()) || s.abbr.toLowerCase() === q.toLowerCase())
      .map((s) => ({ type: "state", label: s.name, go: () => navigate(`/states/${s.abbr}`) })),
    ...countries.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
      .map((c) => ({ type: "country", label: c.name, go: () => navigate("/international") })),
  ].slice(0, 7) : [];

  return (
    <div>
      {/* Running advertisement banner (clearly labeled) */}
      <RunningBanner />
      <PowerStudyBanner testid="home-power-study-banner" />

      {/* HERO */}
      <div className="grid-bg border-b border-white/10">
        <Section className="pt-16 pb-14 sm:pt-24 sm:pb-20">
          <motion.div initial="hidden" animate="show">
            <motion.div custom={0} variants={fade} className="flex items-center gap-2 mb-5">
              <ShieldCheck size={16} className="text-[#84CC16]" />
              <span className="eyebrow">Independent · Source-cited · Non-partisan</span>
            </motion.div>
            <motion.h1 custom={1} variants={fade}
              className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.05]">
              Data centers are changing communities. <span className="text-[#84CC16]">Get the facts behind the claims.</span>
            </motion.h1>
            <motion.p custom={2} variants={fade} className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
              Examine the evidence on water, power, cooling, jobs, taxes, emissions, and community impact — presented as Claim, Evidence, and What's still unknown.
            </motion.p>

            {/* SEARCH */}
            <motion.div custom={3} variants={fade} className="mt-8 max-w-xl relative">
              <div className="flex items-center gap-3 bg-[#111827] border border-white/10 rounded-full px-5 py-3.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30 transition-all">
                <Search size={18} className="text-slate-400 shrink-0" />
                <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Explore data centers by state or country"
                  className="bg-transparent w-full text-white placeholder-slate-500 focus:outline-none" />
              </div>
              {matches.length > 0 && (
                <div data-testid="search-results" className="absolute z-20 mt-2 w-full bg-[#111827] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  {matches.map((m, i) => (
                    <button key={i} onClick={m.go} data-testid={`search-result-${i}`}
                      className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-800 transition-colors">
                      <span className="text-white">{m.label}</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{m.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        </Section>
      </div>

      {/* MAP */}
      <Section className="py-14 sm:py-20">
        <SectionHead eyebrow="Interactive map" title="Explore by state"
          desc="Select a state to see reported facility activity, power, water, cooling, sources, and recent news. Activity levels are qualitative; precise facility totals are only shown where a cited methodology exists." />
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 card p-5 sm:p-8">
            <USGeoMap states={states} onHover={setHover} showTooltip={false} />
          </div>
          <div className="card p-6 min-h-[220px]">
            {hover ? (
              <div data-testid="map-hover-card">
                <p className="eyebrow mb-1">{hover.region}</p>
                <h3 className="font-display font-bold text-2xl text-white">{hover.name}</h3>
                <div className="mt-3"><ConfidenceBadge level={hover.data_confidence} /></div>
                <p className="text-slate-400 text-sm mt-4 line-clamp-4">{hover.summary}</p>
                <button onClick={() => navigate(`/states/${hover.abbr}`)}
                  className="mt-4 inline-flex items-center gap-1.5 text-[#3B82F6] hover:text-[#84CC16] text-sm font-semibold">
                  View {hover.name} <ArrowRight size={15} />
                </button>
              </div>
            ) : (
              <div className="text-slate-400">
                <p className="eyebrow mb-2">Tip</p>
                <p className="text-sm">Hover a state for a quick summary, or click to open the full State Explorer profile.</p>
                <button onClick={() => navigate("/states")} data-testid="browse-all-states-btn"
                  className="mt-4 inline-flex items-center gap-1.5 text-[#3B82F6] hover:text-[#84CC16] text-sm font-semibold">
                  Browse all 50 states <ArrowRight size={15} />
                </button>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* NEWS + EXPLAINERS */}
      <Section className="py-6">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <SectionHead eyebrow="Newsroom" title="Latest briefings"
              right={<button onClick={() => navigate("/newsroom")} className="text-[#3B82F6] hover:text-[#84CC16] text-sm font-semibold inline-flex items-center gap-1.5">All news <ArrowRight size={15} /></button>} />
            {newsStatus && (
              <p className="text-xs text-slate-500 font-mono mb-4">
                Last checked: {fmt(newsStatus.last_checked)} · Last successful update: {fmt(newsStatus.last_success)}
              </p>
            )}
            <div className="space-y-4">
              {news.length === 0 && <div className="card p-6 text-slate-400 text-sm">No briefings published yet. The crawler checks approved sources every 3 hours — an admin can also run it on demand.</div>}
              {news.map((n) => <NewsCard key={n.id} n={n} />)}
            </div>
          </div>
          <div>
            <SectionHead eyebrow="Explainers" title="Start here" />
            <div className="space-y-3">
              {explainers.map((e) => (
                <button key={e.id} onClick={() => navigate("/water-cooling")} data-testid={`explainer-${e.slug}`}
                  className="card p-5 w-full text-left hover:-translate-y-0.5">
                  <p className="eyebrow mb-1">{e.topic}</p>
                  <h4 className="font-display font-semibold text-white">{e.title}</h4>
                  <p className="text-slate-400 text-sm mt-1.5">{e.excerpt}</p>
                </button>
              ))}
            </div>
            <div className="mt-6"><AdSlot testid="home-ad-slot" /></div>
          </div>
        </div>
      </Section>

      {/* CLAIMS vs EVIDENCE */}
      <Section className="py-14 sm:py-20">
        <SectionHead eyebrow="The core method" title="Claims vs. Evidence"
          desc="Every issue is presented in three parts so you can judge for yourself."
          right={<button onClick={() => navigate("/claims")} className="text-[#3B82F6] hover:text-[#84CC16] text-sm font-semibold inline-flex items-center gap-1.5">All claims <ArrowRight size={15} /></button>} />
        <div className="grid md:grid-cols-2 gap-6">
          {claims.slice(0, 2).map((c) => <ClaimCard key={c.id} c={c} />)}
        </div>
      </Section>

      {/* NEWSLETTER */}
      <Section className="py-14">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow mb-2">Free · Weekly · Monday 8am ET</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white">A clear weekly read on data centers</h2>
            <p className="text-slate-400 mt-4 leading-relaxed">Choose a citizen edition (local & plain-language) or a professional edition (grid, policy, projects, cooling, markets). Pick your topics and state. One-click unsubscribe, always.</p>
            <button onClick={() => navigate("/methodology")} className="mt-5 inline-flex items-center gap-1.5 text-[#3B82F6] hover:text-[#84CC16] text-sm font-semibold">
              How we source everything <ExternalLink size={14} />
            </button>
          </div>
          <NewsletterSignup />
        </div>
      </Section>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}

export function NewsCard({ n }) {
  return (
    <a href={n.url} target="_blank" rel="noopener noreferrer" data-testid={`news-card-${n.id}`}
      className="card p-5 block hover:-translate-y-0.5">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#84CC16]">{n.publisher}</span>
        {n.ai_generated && <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 border border-white/10 rounded px-1.5 py-0.5">AI summary</span>}
        {(n.topics || []).slice(0, 2).map((t) => <span key={t} className="text-[10px] text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{t}</span>)}
      </div>
      <h4 className="font-display font-semibold text-white leading-snug flex items-start gap-1.5">{n.title} <ExternalLink size={13} className="text-slate-500 mt-1 shrink-0" /></h4>
      {n.summary && <p className="text-slate-400 text-sm mt-2">{n.summary}</p>}
      {n.why_matters && <p className="text-slate-300 text-sm mt-2"><span className="text-[#84CC16] font-semibold">Why this matters: </span>{n.why_matters}</p>}
      <p className="text-[11px] text-slate-500 font-mono mt-3">Source: {n.publisher}{n.author ? ` · ${n.author}` : ""} · retrieved {fmt(n.retrieved_at)}</p>
    </a>
  );
}

export function ClaimCard({ c }) {
  return (
    <div data-testid={`claim-card-${c.topic}`} className="card p-6">
      <p className="eyebrow mb-3">{c.topic}</p>
      <div className="space-y-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-1">Claim</p>
          <p className="text-white font-semibold">{c.claim}</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#3B82F6]">Evidence</p>
            <ConfidenceBadge level={c.evidence_confidence} />
          </div>
          <p className="text-slate-300 text-sm">{c.evidence}</p>
          <div className="mt-2 space-y-1">{(c.sources || []).map((s, i) => <div key={i}><SourceLink source={s} /></div>)}</div>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">What's still unknown</p>
          <p className="text-slate-400 text-sm">{c.unknown}</p>
        </div>
      </div>
    </div>
  );
}
